
import React from 'react';
import { CharacterData, ActionName, MAX_REMOVE_LIMIT, MAX_DUPLICATE_LIMIT, CustomAction } from '../config';

interface CharacterCardProps {
    character: CharacterData;
    totalScore: number;
    maxLimit: number;
    onCountChange: (charId: string, actionName: ActionName, count: number) => void;
    onNameChange: (charId: string, name: string) => void;
    onResetCharacter: (charId: string, name: string) => void;
    actionOrder: ActionName[];
    customActions: CustomAction[];
}

const ActionInput: React.FC<{
    charId: string;
    actionName: ActionName;
    displayName: string; // prop 用於顯示標籤
    count: number;
    disableIncrement: boolean; // prop 用於禁用增加按鈕
    onCountChange: (charId: string, actionName: ActionName, count: number) => void;
}> = ({ charId, actionName, displayName, count, disableIncrement, onCountChange }) => {
    
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(e.target.value, 10);
        // 手動輸入時，若被禁用增加且輸入值大於當前值，這裡暫不做強制阻擋，
        // 主要依賴 UI 的 + 按鈕禁用與視覺回饋。
        onCountChange(charId, actionName, isNaN(value) ? 0 : value);
    };

    const handleIncrement = () => {
        if (!disableIncrement) {
            onCountChange(charId, actionName, count + 1);
        }
    };
    
    const handleDecrement = () => {
        if (count > 0) {
            onCountChange(charId, actionName, count - 1);
        }
    };
    
    return (
        <div className="flex flex-col p-2 bg-gray-50 dark:bg-slate-700/50 rounded-lg h-full justify-between transition-colors duration-300 min-w-0">
            <label htmlFor={`${charId}-${actionName}`} className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 truncate text-center w-full block" title={displayName}>
                {displayName}
            </label>
            <div className="flex items-center gap-1 w-full">
                <button type="button" onClick={handleDecrement}
                        className="w-8 h-8 flex items-center justify-center bg-gray-200 dark:bg-slate-700/50 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-slate-600/50 transition duration-150 text-xl font-bold p-1 leading-none active:scale-95 shrink-0">
                        &minus;
                </button>
                <input type="number" id={`${charId}-${actionName}`} value={count || 0} min="0"
                       onChange={handleInputChange}
                       className="flex-1 min-w-[1.5rem] py-2 px-1 bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-slate-600 rounded-md text-base text-center font-mono focus:border-indigo-500 focus:ring-indigo-500 transition-colors duration-300"/>
                <button type="button" onClick={handleIncrement} disabled={disableIncrement}
                        className={`w-8 h-8 flex items-center justify-center rounded-lg transition duration-150 text-xl font-bold p-1 leading-none shrink-0 ${
                            disableIncrement 
                            ? 'bg-gray-100 dark:bg-slate-800 text-gray-400 dark:text-gray-600 cursor-not-allowed' 
                            : 'bg-gray-200 dark:bg-slate-700/50 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-slate-600/50 active:scale-95'
                        }`}>
                        +
                </button>
            </div>
        </div>
    );
};

const CharacterCard: React.FC<CharacterCardProps> = ({ character, totalScore, maxLimit, onCountChange, onNameChange, onResetCharacter, actionOrder, customActions }) => {
    const isOverLimit = totalScore > maxLimit;
    const percentage = maxLimit > 0 ? Math.min((totalScore / maxLimit) * 100, 100) : 0;
    const remainingPoints = maxLimit - totalScore;
    const progressBarColor = isOverLimit ? 'bg-red-500' : 'bg-indigo-500';
    const totalPointsColor = isOverLimit ? 'text-red-600 dark:text-red-400' : 'text-gray-800 dark:text-gray-200';

    // 計算限制相關數據
    const totalRemoves = (character.counts["移除起始卡"] || 0) + (character.counts["移除其他卡"] || 0);
    const totalDuplicates = character.counts["複製卡片"] || 0;

    // 狀態顏色
    const removeLimitClass = totalRemoves >= MAX_REMOVE_LIMIT ? 'text-orange-600 dark:text-orange-400 font-bold' : 'text-gray-500 dark:text-gray-400';
    const duplicateLimitClass = totalDuplicates >= MAX_DUPLICATE_LIMIT ? 'text-orange-600 dark:text-orange-400 font-bold' : 'text-gray-500 dark:text-gray-400';

    return (
        <div className="card bg-white dark:bg-slate-800 p-4 sm:p-5 rounded-xl flex flex-col transition-colors duration-300 h-full">
            <div className="flex justify-between items-center mb-4 pb-2 border-b border-gray-200 dark:border-gray-700 gap-4">
                <input 
                    type="text" 
                    value={character.name}
                    onChange={(e) => onNameChange(character.id, e.target.value)}
                    placeholder="輸入角色名稱"
                    className="name-input text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-200 border-b-2 focus:border-indigo-500 w-full p-0 transition duration-150 bg-transparent"
                />
                <button type="button" onClick={() => onResetCharacter(character.id, character.name)}
                    className="px-3 py-1.5 sm:px-4 sm:py-2 text-sm font-semibold text-white bg-red-500 dark:bg-red-600/80 rounded-lg hover:bg-red-600 dark:hover:bg-red-600 transition duration-150 active:scale-95 flex-shrink-0 shadow">
                    重置
                </button>
            </div>
            
            <div className="mb-4">
                <div className="flex justify-between items-end mb-1.5">
                    <div>
                        <p className={`${totalPointsColor} text-sm`}>
                            <span className="font-medium text-gray-700 dark:text-gray-300">累計</span>
                            <span className="text-2xl font-extrabold ml-1 transition-colors duration-300">{totalScore}</span>
                            <span className="font-normal text-gray-500 dark:text-gray-400"> pt</span>
                        </p>
                        {/* 限制提示小字 */}
                        <div className="flex gap-2 text-xs mt-0.5">
                            <span className={removeLimitClass}>移除 ({totalRemoves}/{MAX_REMOVE_LIMIT})</span>
                            <span className="text-gray-300 dark:text-gray-600">|</span>
                            <span className={duplicateLimitClass}>複製 ({totalDuplicates}/{MAX_DUPLICATE_LIMIT})</span>
                        </div>
                    </div>

                    <p className="font-medium text-gray-500 dark:text-gray-400 text-sm mb-1"> {/* mb-1 對齊視覺重心 */}
                        剩餘 {remainingPoints} pt
                    </p>
                </div>
                <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2" role="presentation">
                    <div 
                        className={`h-2 rounded-full transition-all duration-300 ${progressBarColor}`}
                        style={{ width: `${percentage}%` }}
                        role="progressbar"
                        aria-valuenow={totalScore}
                        aria-valuemin={0}
                        aria-valuemax={maxLimit}
                        aria-label={`Score: ${totalScore} out of ${maxLimit}`}
                    ></div>
                </div>
            </div>


            <div className="grid grid-cols-2 gap-3">
                {actionOrder.map(name => {
                    const custom = customActions.find(c => c.id === name);
                    const displayName = custom ? custom.name : name;
                    let disableIncrement = false;

                    // 檢查是否達到上限以禁用按鈕
                    if (name === "移除起始卡" || name === "移除其他卡") {
                        disableIncrement = totalRemoves >= MAX_REMOVE_LIMIT;
                    } else if (name === "複製卡片") {
                        disableIncrement = totalDuplicates >= MAX_DUPLICATE_LIMIT;
                    }

                    return (
                        <ActionInput
                            key={name}
                            charId={character.id}
                            actionName={name}
                            displayName={displayName}
                            count={character.counts[name] || 0}
                            disableIncrement={disableIncrement}
                            onCountChange={onCountChange}
                        />
                    );
                })}
            </div>
        </div>
    );
};

export default CharacterCard;
